import React, { useState } from 'react';
import LineChart from './charts/LineChart';
import Sparkline from './charts/Sparkline';
import {
  GUIDE, bandLabel, changeSinceFirst, dayMonth, moveWords,
} from './lib/activityFormat';

/** Line-chart points. A null value stays in its slot so the line breaks there. */
export function chartPoints(points) {
  return (points || []).map((point, index) => ({
    key: `${(point && point.at) || 'none'}-${index}`,
    label: point && point.at ? dayMonth(point.at) : '—',
    value: point && point.value != null ? point.value : null,
  }));
}

/**
 * Headline, optional line, and the change since the first point.
 * `chart` draws the full line; otherwise a sparkline when there are two points.
 */
export const ScoreReadout = ({
  score, chart = false, caption, facts, chartSize = 'default', changeText,
}) => {
  if (!score || score.current == null) {
    return (
      <div className="td-act-score">
        <p className="td-act-meta">No score yet</p>
        {caption ? <p className="td-act-meta">{caption}</p> : null}
        {facts ? <p className="td-act-meta">{facts}</p> : null}
        <p className="td-act-note">{GUIDE}</p>
      </div>
    );
  }
  const change = changeText !== undefined ? changeText : changeSinceFirst(score.points);
  const values = (score.points || []).map((point) => (point ? point.value : null));
  const drawn = chartPoints(score.points);
  const band = bandLabel(score.band);
  return (
    <div className="td-act-score">
      <div className="td-act-score__head">
        <span className="td-act-score__n">{score.current}</span>
        {band ? <span className="td-act-score__band">{band}</span> : null}
      </div>
      {!chart && values.filter((value) => value != null).length >= 2 ? (
        <Sparkline
          values={values}
          max={100}
          label={`Scores out of 100: ${values.filter((value) => value != null).join(', ')}`}
        />
      ) : null}
      {chart && drawn.some((point) => point.value != null) ? (
        <LineChart
          points={drawn}
          size={chartSize}
          format={(value) => String(value)}
          ariaLabel={`Scores out of 100: ${drawn.map((point) => `${point.label} ${point.value == null ? 'no score' : point.value}`).join(', ')}`}
        />
      ) : null}
      {change ? <p className="td-act-meta">{change}</p> : null}
      {facts ? <p className="td-act-meta">{facts}</p> : null}
      {caption ? <p className="td-act-meta">{caption}</p> : null}
      <p className="td-act-note">{GUIDE}</p>
    </div>
  );
};

/** One axis group. Several groups are tabs. A group with no passes says so. */
export const AxisPanel = ({ groups, chartSize = 'default' }) => {
  const [tab, setTab] = useState(0);
  if (!groups || !groups.length) return <p className="td-act-meta">No scoring axes yet.</p>;
  const group = groups[Math.min(tab, groups.length - 1)];
  const empty = !group.series.length || group.series.every((series) => !series.points.length);
  return (
    <div>
      {groups.length > 1 ? (
        <div className="td-act-toggle" role="tablist" aria-label="Questions">
          {groups.map((item, index) => (
            <button
              key={item.element || item.label}
              type="button"
              role="tab"
              aria-selected={index === tab}
              onClick={() => setTab(index)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
      {empty ? <p className="td-act-meta">No passes yet.</p> : (
        <div className={chartSize === 'small' ? 'td-act-axes td-act-axes--small' : 'td-act-axes'}>
          {group.series.map((series) => {
            const latest = [...series.points].reverse().find((point) => point.value != null);
            const word = group.element && latest ? moveWords(latest.value) : null;
            const drawn = chartPoints(series.points);
            return (
              <div key={series.key}>
                <div className="td-act-subhead">{word ? `${series.label} · ${word}` : series.label}</div>
                {drawn.some((point) => point.value != null) ? (
                  <LineChart
                    points={drawn}
                    size={chartSize}
                    format={(value) => String(value)}
                    ariaLabel={`${series.label}: ${drawn.map((point) => (point.value == null ? 'no score' : point.value)).join(', ')}`}
                  />
                ) : <p className="td-act-meta">No score for this move yet.</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** A collapsed panel. The controlled element stays in the DOM. */
export const Collapse = ({ id, open, onToggle, label, children }) => (
  <>
    <button
      type="button"
      className="td-act-vhbtn"
      aria-expanded={open}
      aria-controls={id}
      onClick={onToggle}
    >
      {label}
      <span aria-hidden="true">{open ? ' ▴' : ' ▾'}</span>
    </button>
    <div id={id} hidden={!open}>{children}</div>
  </>
);
